import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Divider,
  Alert
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { getCurrencySymbol } from '../../utils/currency';

const getStatusColor = (status) => {
  const colors = {
    succeeded: 'success',
    completed: 'success',
    refunded: 'success',
    pending: 'warning',
    processing: 'info',
    failed: 'error',
    cancelled: 'default',
    requires_action: 'warning'
  };
  return colors[status] || 'default';
};

const getStatusIcon = (status) => {
  const icons = {
    succeeded: <CheckCircleIcon />,
    completed: <CheckCircleIcon />,
    refunded: <CheckCircleIcon />,
    pending: <ScheduleIcon />,
    processing: <ScheduleIcon />,
    failed: <ErrorIcon />,
    cancelled: <ErrorIcon />,
    requires_action: <ScheduleIcon />
  };
  return icons[status] || <ScheduleIcon />;
};

const getPaymentMethodLabel = (method, t) => {
  const key = method?.toLowerCase();
  if (!key) return t('renterRefund.na', 'N/A');
  return t(`renterRefund.paymentMethodValues.${key}`, method);
};

/**
 * Composant pour afficher le récapitulatif du remboursement pour le locataire
 * Affiché uniquement au locataire dans la section résolution d'un litige résolu en sa faveur
 */
const RenterRefundPaymentSummary = ({ payment }) => {
  const { t, i18n } = useTranslation('disputes');
  const dateLocale = i18n?.language && i18n.language.startsWith('fr') ? fr : enUS;

  if (!payment) {
    return null;
  }

  const currencySymbol = getCurrencySymbol(payment.currency);
  const amount = parseFloat(payment.amount || 0);
  // Parse refunded_amount first: if "0.00" string, parseFloat gives 0 (falsy), so fallback to amount
  const parsedRefundedAmount = parseFloat(payment.refunded_amount || 0);
  const refundedAmount = parsedRefundedAmount > 0 ? parsedRefundedAmount : amount;
  const isRefund = payment.status === 'refunded' || parsedRefundedAmount > 0;

  const refundTransactionId = payment.refund_transaction_id || payment.refund_id;
  const refundedAt = payment.refunded_at || payment.refundedAt || payment.metadata?.refunded_at || payment.metadata?.refundedAt;
  const createdAt = payment.transaction_date || payment.createdAt || payment.created_at;
  const failureReason = payment.failure_reason || payment.failure_message;

  return (
    <Card
      sx={{
        mt: 2,
        bgcolor: 'background.paper',
        borderLeft: 6,
        borderLeftColor: 'success.main',
        borderRadius: 2,
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? '0 8px 20px rgba(0,0,0,0.6)'
            : '0 14px 40px rgba(76,175,80,0.08)'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <AccountBalanceIcon color="success" />
          <Typography variant="h6" fontWeight="bold">
            {t('renterRefund.title', 'Remboursement reçu')}
          </Typography>
        </Box>

        <Alert severity="success" icon={<ReceiptIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t(
              'renterRefund.description',
              'Votre remboursement a été traité suite à la résolution du litige en votre faveur.'
            )}
          </Typography>
        </Alert>

        <Grid container spacing={2}>
          {/* Statut du payment */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('renterRefund.status', 'Statut')}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  icon={getStatusIcon(payment.status)}
                  label={t(`paymentStatus.${payment.status}`, payment.status)}
                  color={getStatusColor(payment.status)}
                  size="small"
                />
              </Box>
            </Box>
          </Grid>

          {/* Méthode de paiement */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('renterRefund.paymentMethod', 'Méthode de remboursement')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <CreditCardIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {getPaymentMethodLabel(payment.payment_method, t)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Montant remboursé */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {isRefund 
                  ? t('renterRefund.refundedAmount', 'Montant remboursé')
                  : t('renterRefund.amount', 'Montant')}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {refundedAmount.toFixed(2)} {currencySymbol}
              </Typography>
            </Box>
          </Grid>

          {/* Type de payment */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('renterRefund.type', 'Type')}
              </Typography>
              <Typography variant="body2">
                {t(`renterRefund.paymentTypeValues.${payment.payment_type}`, payment.payment_type || t('renterRefund.na', 'N/A'))}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* ID de paiement Stripe */}
          {payment.stripe_payment_intent_id && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {t('renterRefund.stripePaymentIntentId', 'ID Stripe Payment Intent')}
                </Typography>
                <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                  {payment.stripe_payment_intent_id}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* ID de refund Stripe */}
          {refundTransactionId && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {t('renterRefund.stripeRefundId', 'ID Stripe Refund')}
                </Typography>
                <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                  {refundTransactionId}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Date de remboursement */}
          {refundedAt && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {t('renterRefund.refundedAt', 'Date de remboursement')}
                </Typography>
                <Typography variant="body2">
                  {format(new Date(refundedAt), 'dd MMMM yyyy à HH:mm', { locale: dateLocale })}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Date de création */}
          {createdAt && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {t('renterRefund.createdAt', 'Date de création')}
                </Typography>
                <Typography variant="body2">
                  {format(new Date(createdAt), 'dd MMMM yyyy à HH:mm', { locale: dateLocale })}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Raison d'échec si applicable */}
          {payment.status === 'failed' && failureReason && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  {t('renterRefund.failureReason', 'Raison de l\'échec')}:
                </Typography>
                <Typography variant="body2">
                  {failureReason}
                </Typography>
              </Alert>
            </Grid>
          )}

          {/* Note métadonnées si présentes */}
          {payment.metadata?.dispute_resolution_note && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  {t('renterRefund.note', 'Note')}:
                </Typography>
                <Typography variant="body2">
                  {payment.metadata.dispute_resolution_note}
                </Typography>
              </Alert>
            </Grid>
          )}
        </Grid>

        {/* Note délai bancaire */}
        <Alert severity="info" icon={false} sx={{ mt: 3, bgcolor: 'info.lighter' }}>
          <Typography variant="caption" color="text.secondary">
            {t(
              'renterRefund.bankingDelayNotice',
              'ℹ️ Le remboursement apparaîtra sur votre compte bancaire sous 3 à 10 jours ouvrables selon votre banque.'
            )}
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default RenterRefundPaymentSummary;
